import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { notificationsAPI } from '../lib/api';
import { Button } from './ui/button';
import { CheckCircle, AlertTriangle, XCircle, Info, Shield, X, ChevronDown, ChevronUp } from 'lucide-react';

const TYPE_CONFIG = {
  success: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', label: 'Успех' },
  warning: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/15', border: 'border-amber-500/30', label: 'Предупреждение' },
  error: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/15', border: 'border-red-500/30', label: 'Ошибка' },
  info: { icon: Info, color: 'text-sky-400', bg: 'bg-sky-500/15', border: 'border-sky-500/30', label: 'Информация' },
  security: { icon: Shield, color: 'text-orange-400', bg: 'bg-orange-500/15', border: 'border-orange-500/30', label: 'Безопасность' },
};

const PRIORITY_BADGE = {
  high: { label: 'Важно', cls: 'bg-red-500/20 text-red-400 border-red-500/30' },
  medium: { label: 'Обычный', cls: 'bg-sky-500/20 text-sky-400 border-sky-500/30' },
  low: { label: 'Низкий', cls: 'bg-muted text-muted-foreground border-border/50' },
};

// Detects mobile width — used to switch between fullscreen native-scroll mode (mobile)
// and bounded modal mode (desktop).
function useIsMobile(breakpoint = 640) {
  const [mobile, setMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return mobile;
}

export default function NotificationPopup() {
  const [popups, setPopups] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const scrollContainerRef = useRef(null);
  const isMobile = useIsMobile();

  const fetchPopups = useCallback(async () => {
    try {
      const { data } = await notificationsAPI.list();
      const undismissed = (data.popup_notifications || []);
      setPopups(undismissed);
    } catch {} finally { setLoaded(true); }
  }, []);

  useEffect(() => { fetchPopups(); }, [fetchPopups]);

  // Lock background scroll while popup is open
  useEffect(() => {
    if (popups.length === 0) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [popups.length]);

  const handleDismiss = async (id) => {
    try { await notificationsAPI.dismiss(id); } catch {}
    if (currentIndex < popups.length - 1) {
      setCurrentIndex(prev => prev + 1);
      if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
    } else {
      setPopups([]);
      setCurrentIndex(0);
    }
  };

  const handleDismissAll = async () => {
    try { await notificationsAPI.dismissAll(); } catch {}
    setPopups([]);
    setCurrentIndex(0);
  };

  const scrollBy = (offset) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollBy({ top: offset, behavior: 'smooth' });
  };

  if (!loaded || popups.length === 0) return null;
  const notif = popups[currentIndex];
  if (!notif) return null;
  const config = TYPE_CONFIG[notif.type] || TYPE_CONFIG.info;
  const Icon = config.icon;
  const priorityInfo = PRIORITY_BADGE[notif.priority] || PRIORITY_BADGE.medium;

  /* ════════════════════════════════════════════════════════════════
     MOBILE: full-screen with native document-level scrolling.
     This is the most reliable approach — no nested scroll bugs on any
     mobile browser (Huawei Browser, old Chrome WebView, Samsung etc).
     ════════════════════════════════════════════════════════════════ */
  if (isMobile) {
    return (
      <AnimatePresence>
        <motion.div
          key="notification-fs"
          ref={scrollContainerRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-background"
          style={{
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
          }}
          data-testid="notification-popup-overlay"
        >
          {/* Top accent */}
          <div className={`h-1.5 w-full ${config.bg.replace('/15', '')} sticky top-0 z-10`} />

          {/* Sticky header */}
          <div
            className="sticky top-1.5 z-10 bg-background/95 backdrop-blur-md border-b border-border/40 px-5 py-3 flex items-start justify-between gap-3"
            data-testid="notification-popup-header"
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className={`w-10 h-10 rounded-xl ${config.bg} border ${config.border} flex items-center justify-center flex-shrink-0`}>
                <Icon size={20} className={config.color} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-semibold uppercase tracking-wider ${config.color}`}>{config.label}</span>
                  {notif.priority === 'high' && (
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full border font-medium ${priorityInfo.cls}`}>{priorityInfo.label}</span>
                  )}
                  {popups.length > 1 && (
                    <span className="text-[10px] text-muted-foreground font-medium bg-secondary/50 px-2 py-0.5 rounded-lg whitespace-nowrap">
                      {currentIndex + 1} / {popups.length}
                    </span>
                  )}
                </div>
                <h3 className="text-[15px] font-bold mt-0.5 break-words leading-tight">{notif.title}</h3>
              </div>
            </div>
            <button
              onClick={() => handleDismiss(notif.id)}
              className="w-10 h-10 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground bg-secondary/40 hover:bg-secondary/80 active:bg-secondary transition-colors flex-shrink-0"
              data-testid="notification-popup-close-btn"
              aria-label="Закрыть"
            >
              <X size={22} />
            </button>
          </div>

          {/* Body — native page flow scrolling */}
          <div className="px-5 py-5 pb-32" data-testid="notification-popup-body">
            <p className="text-[15px] text-foreground/95 leading-relaxed whitespace-pre-line break-words">
              {notif.message}
            </p>
          </div>

          {/* Floating scroll buttons (right side) — explicit, large, hard to miss */}
          <div className="fixed right-3 top-1/2 -translate-y-1/2 z-20 flex flex-col gap-2" data-testid="notification-popup-scroll-controls">
            <button
              onClick={() => scrollBy(-300)}
              className="w-12 h-12 rounded-full bg-primary/90 text-primary-foreground shadow-lg flex items-center justify-center active:scale-95 transition-transform"
              aria-label="Прокрутить вверх"
              data-testid="notif-scroll-up-btn"
              type="button"
            >
              <ChevronUp size={22} />
            </button>
            <button
              onClick={() => scrollBy(300)}
              className="w-12 h-12 rounded-full bg-primary/90 text-primary-foreground shadow-lg flex items-center justify-center active:scale-95 transition-transform"
              aria-label="Прокрутить вниз"
              data-testid="notif-scroll-down-btn"
              type="button"
            >
              <ChevronDown size={22} />
            </button>
          </div>

          {/* Sticky footer with buttons */}
          <div
            className="fixed left-0 right-0 bottom-0 z-10 bg-background/95 backdrop-blur-md border-t border-border/40 px-5 py-3 flex items-center justify-between gap-3"
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
          >
            {popups.length > 1 ? (
              <button
                onClick={handleDismissAll}
                className="text-[12px] text-muted-foreground hover:text-foreground py-2 px-2 underline-offset-2 hover:underline"
                data-testid="dismiss-all-btn"
              >
                Закрыть все ({popups.length})
              </button>
            ) : <div />}
            <Button
              onClick={() => handleDismiss(notif.id)}
              className="px-8 h-12 font-semibold text-base"
              data-testid="dismiss-notification-btn"
            >
              Понятно
            </Button>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  /* ════════════════════════════════════════════════════════════════
     DESKTOP: classic centered modal with inner scroll (works fine here)
     ════════════════════════════════════════════════════════════════ */
  return (
    <AnimatePresence>
      <motion.div
        key="notification-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0,0,0,0.72)' }}
        data-testid="notification-popup-overlay"
      >
        <motion.div
          key={notif.id}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="w-full max-w-md rounded-2xl border border-border/50 bg-card shadow-2xl flex flex-col"
          style={{ maxHeight: '85vh', minHeight: '480px' }}
          data-testid="notification-popup-card"
        >
          <div className={`h-1 w-full ${config.bg.replace('/15', '')} rounded-t-2xl flex-shrink-0`} />
          <div className="px-6 pt-5 pb-3 flex items-start justify-between gap-3 flex-shrink-0 border-b border-border/30">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-10 h-10 rounded-xl ${config.bg} border ${config.border} flex items-center justify-center flex-shrink-0`}>
                <Icon size={20} className={config.color} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-semibold uppercase tracking-wider ${config.color}`}>{config.label}</span>
                  {notif.priority === 'high' && (
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full border font-medium ${priorityInfo.cls}`}>{priorityInfo.label}</span>
                  )}
                </div>
                <h3 className="text-base font-bold mt-0.5 break-words leading-tight">{notif.title}</h3>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {popups.length > 1 && (
                <span className="text-[10px] text-muted-foreground font-medium bg-secondary/50 px-2 py-1 rounded-lg whitespace-nowrap">
                  {currentIndex + 1} / {popups.length}
                </span>
              )}
              <button onClick={() => handleDismiss(notif.id)} className="w-9 h-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/70" data-testid="notification-popup-close-btn" aria-label="Закрыть">
                <X size={20} />
              </button>
            </div>
          </div>
          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-6 py-4" data-testid="notification-popup-body">
            <p className="text-sm text-foreground/95 leading-relaxed whitespace-pre-line break-words">{notif.message}</p>
          </div>
          <div className="px-6 py-4 flex items-center justify-between gap-3 flex-shrink-0 border-t border-border/30">
            {popups.length > 1 ? (
              <button onClick={handleDismissAll} className="text-[11px] text-muted-foreground hover:text-foreground" data-testid="dismiss-all-btn">
                Закрыть все ({popups.length})
              </button>
            ) : <div />}
            <Button onClick={() => handleDismiss(notif.id)} className="px-6 h-10 font-semibold" data-testid="dismiss-notification-btn">Понятно</Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export function NotifTypeIcon({ type, size = 14 }) {
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.info;
  const Icon = config.icon;
  return <Icon size={size} className={config.color} />;
}

export { TYPE_CONFIG };
